// Tabbar — Phase 12 Plan 12-00 Task 2 (telegram-ui native Tabbar rewrite).
//
// REVERSES the Phase 4 decision ("We do NOT use telegram-ui's Tabbar"): UI-SPEC
// §6 / State-of-the-Art mandates the native telegram-ui <Tabbar> for a native
// Telegram look (RESEARCH Pattern 1, PATTERNS §Tabbar rewrite).
//
// PRESERVED (critical — do NOT regress, Security V4 / threat T-12-00-01):
//   role-visibility matrix:
//     owner       → 7 tabs (Dashboard/Trips/Drivers/Reports/Settings + Members/Carriers)
//     dispatcher  → 5 tabs (no Members/Carriers)
//     driver/other → null (driver Mini App uses its own UI shell)
//   data-testid attributes (telegram-ui forwards via ...restProps), labelKey via t().
//
// CHANGED vs plain-DOM version:
//   - native <Tabbar> + <Tabbar.Item> (each with VISIBLE text label + icon —
//     UI-SPEC §Дополнение, icon-only forbidden);
//   - decodeJwtPayload consolidated onto canonical lib/jwt (local copy removed);
//   - bottom gap via safeArea helper (env(safe-area-inset-bottom) fallback,
//     Pitfall 1 / bug #93);
//   - active tab = accent (UI-SPEC §5 reserved #1);
//   - hardcode hex (#1E63CE/#64748B/#e2e8f0) → var(--tg-theme-*).

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { Tabbar as TgTabbar } from "@telegram-apps/telegram-ui";

import { useAuth } from "../auth/useAuth";
import { decodeJwtPayload } from "../lib/jwt";
import { SAFE_AREA_BOTTOM } from "../lib/safeArea";
import { TOUCH_TARGET_MIN } from "../lib/tokens";

export type DispatcherTabKey =
  | "dashboard"
  | "trips"
  | "drivers"
  | "reports"
  | "settings"
  | "members"
  | "carriers";

interface TabDef {
  key: DispatcherTabKey;
  path: string;
  labelKey: string;
  /** Owner-only tab; hidden for dispatcher role. */
  ownerOnly?: boolean;
  /** Collapse into "More" overflow on mobile (owner-only tabs do this). */
  inMoreOnMobile?: boolean;
}

const TABS: TabDef[] = [
  {
    key: "dashboard",
    path: "/dispatcher/dashboard",
    labelKey: "dispatcher:tabbar.dashboard",
  },
  { key: "trips", path: "/dispatcher/trips", labelKey: "dispatcher:tabbar.trips" },
  { key: "drivers", path: "/dispatcher/drivers", labelKey: "dispatcher:tabbar.drivers" },
  { key: "reports", path: "/dispatcher/reports", labelKey: "dispatcher:tabbar.reports" },
  { key: "settings", path: "/dispatcher/settings", labelKey: "dispatcher:tabbar.settings" },
  {
    key: "members",
    path: "/dispatcher/members",
    labelKey: "dispatcher:tabbar.members",
    ownerOnly: true,
    inMoreOnMobile: true,
  },
  {
    key: "carriers",
    path: "/dispatcher/carriers",
    labelKey: "dispatcher:tabbar.carriers",
    ownerOnly: true,
    inMoreOnMobile: true,
  },
];

// Minimal 28×28 line icons (telegram-ui expects icon ReactNode at 28×28).
// currentColor → inherits accent/hint from telegram-ui Tabbar.Item active state.
// `title` gives each svg an accessible name (the visible `text` label is the
// primary affordance; <title> satisfies a11y + screen-reader fallback).
function iconPaths(tab: DispatcherTabKey | "more"): React.JSX.Element {
  switch (tab) {
    case "dashboard":
      return (
        <>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </>
      );
    case "trips":
      return (
        <>
          <path d="M3 7h13l3 4v6H3z" />
          <circle cx="7" cy="17" r="2" />
          <circle cx="16" cy="17" r="2" />
        </>
      );
    case "drivers":
      return (
        <>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </>
      );
    case "reports":
      return (
        <>
          <path d="M5 3h9l5 5v13H5z" />
          <path d="M14 3v5h5" />
          <path d="M9 13h6M9 17h6" />
        </>
      );
    case "settings":
      return (
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
        </>
      );
    case "members":
      return (
        <>
          <circle cx="9" cy="9" r="3" />
          <path d="M3 19a6 6 0 0 1 12 0" />
          <path d="M16 7a3 3 0 0 1 0 6M17 19a6 6 0 0 0-2-4.5" />
        </>
      );
    case "carriers":
      return (
        <>
          <rect x="3" y="8" width="11" height="8" rx="1" />
          <path d="M14 11h4l3 3v2h-7z" />
          <circle cx="7" cy="18" r="1.8" />
          <circle cx="17" cy="18" r="1.8" />
        </>
      );
    default:
      // "more" — horizontal ellipsis.
      return (
        <>
          <circle cx="5" cy="12" r="1.5" fill="currentColor" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
          <circle cx="19" cy="12" r="1.5" fill="currentColor" />
        </>
      );
  }
}

function TabIcon({
  tab,
  title,
}: {
  tab: DispatcherTabKey | "more";
  title: string;
}): React.JSX.Element {
  return (
    <svg
      width={28}
      height={28}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-hidden="true"
    >
      <title>{title}</title>
      {iconPaths(tab)}
    </svg>
  );
}

export function Tabbar(): React.JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { jwt } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);

  const role = useMemo<string | null>(() => {
    return decodeJwtPayload(jwt)?.role ?? null;
  }, [jwt]);

  // Tabbar only renders for dispatcher-level roles (owner + dispatcher).
  if (role !== "owner" && role !== "dispatcher") {
    return null;
  }

  const isOwner = role === "owner";
  const visibleTabs = TABS.filter((tab) => !tab.ownerOnly || isOwner);

  // Main row = tabs not in More overflow; More overflow = ownerOnly tabs.
  const mainTabs = visibleTabs.filter((tab) => !tab.inMoreOnMobile);
  const moreTabs = visibleTabs.filter((tab) => tab.inMoreOnMobile);

  function isActive(path: string): boolean {
    return location.pathname.startsWith(path);
  }

  function handleNavigate(path: string): void {
    setMoreOpen(false);
    navigate(path);
  }

  // telegram-ui <Tabbar> expects an array of <Tabbar.Item>. data-testid /
  // aria-current / onClick flow through ...restProps to the underlying <button>.
  const items = mainTabs.map((tab) => (
    <TgTabbar.Item
      key={tab.key}
      text={t(tab.labelKey, tab.key) as string}
      selected={isActive(tab.path)}
      data-testid={`tabbar-tab-${tab.key}`}
      aria-current={isActive(tab.path) ? "page" : undefined}
      onClick={() => handleNavigate(tab.path)}
    >
      <TabIcon tab={tab.key} title={t(tab.labelKey, tab.key) as string} />
    </TgTabbar.Item>
  ));

  if (moreTabs.length > 0) {
    items.push(
      <TgTabbar.Item
        key="more"
        text={t("dispatcher:tabbar.more", "More") as string}
        selected={moreOpen}
        data-testid="tabbar-tab-more"
        aria-haspopup="true"
        aria-expanded={moreOpen}
        onClick={() => setMoreOpen((v) => !v)}
      >
        <TabIcon tab="more" title={t("dispatcher:tabbar.more", "More") as string} />
      </TgTabbar.Item>,
    );
  }

  return (
    <>
      <TgTabbar
        data-testid="tabbar"
        aria-label={t("dispatcher:tabbar.aria_label", "Main navigation") as string}
        // Pitfall 1 / bug #93 — stable env(safe-area-inset-bottom) fallback so
        // the home-indicator never overlaps the bar (SDK value is unreliable).
        style={{ paddingBottom: SAFE_AREA_BOTTOM }}
      >
        {items}
      </TgTabbar>

      {moreOpen && moreTabs.length > 0 && (
        <div
          data-testid="tabbar-more-menu"
          role="menu"
          style={{
            position: "fixed",
            right: "var(--tgui--spacing, 8px)",
            bottom: 64,
            backgroundColor: "var(--tgui--bg_color, var(--tg-theme-bg-color, #fff))",
            border:
              "1px solid var(--tgui--section_separator_color, var(--tg-theme-section-separator-color, #e2e8f0))",
            borderRadius: 12,
            boxShadow: "0 4px 16px rgba(0,0,0,0.16)",
            padding: 4,
            minWidth: 168,
            zIndex: 1100,
          }}
        >
          {moreTabs.map((tab) => (
            <button
              type="button"
              key={tab.key}
              data-testid={`tabbar-more-${tab.key}`}
              role="menuitem"
              aria-current={isActive(tab.path) ? "page" : undefined}
              onClick={() => handleNavigate(tab.path)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                minHeight: TOUCH_TARGET_MIN,
                border: "none",
                background: isActive(tab.path)
                  ? "var(--tgui--secondary_fill, var(--tg-theme-secondary-bg-color, #E0E7FF))"
                  : "transparent",
                cursor: "pointer",
                fontSize: 15,
                textAlign: "left",
                padding: "8px 10px",
                borderRadius: 8,
                color: isActive(tab.path)
                  ? "var(--tgui--link_color, var(--tg-theme-link-color, #1E63CE))"
                  : "var(--tgui--text_color, var(--tg-theme-text-color, #1F2937))",
              }}
            >
              <span style={{ display: "inline-flex", width: 22, height: 22 }} aria-hidden>
                <TabIcon tab={tab.key} title={t(tab.labelKey, tab.key) as string} />
              </span>
              {t(tab.labelKey, tab.key) as string}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Layout wrapper — renders children + persistent Tabbar at bottom. */
export function TabbarLayout({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <>
      <div style={{ paddingBottom: 80 }}>{children}</div>
      <Tabbar />
    </>
  );
}
